import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';

function CatalogManagement() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New Product Form State
  const [showProductModal, setShowProductModal] = useState(false);
  const [prodCode, setProdCode] = useState('');
  const [prodName, setProdName] = useState('');
  const [prodDesc, setProdDesc] = useState('');
  const [prodType, setProdType] = useState('ADVERTISING');

  // New Package Form State
  const [showPkgModal, setShowPkgModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [pkgCode, setPkgCode] = useState('');
  const [pkgName, setPkgName] = useState('');
  const [pkgDesc, setPkgDesc] = useState('');
  const [pkgDays, setPkgDays] = useState(7);
  const [pkgPriceRupees, setPkgPriceRupees] = useState(2500);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, pkgRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/commercial/products`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/admin/commercial/packages`, { headers: getAuthHeaders() })
      ]);
      if (pRes.ok) setProducts(await pRes.json());
      if (pkgRes.ok) setPackages(await pkgRes.json());
      setError(null);
    } catch (e) {
      setError('Failed to fetch catalog items');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!prodCode || !prodName) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          code: prodCode.toUpperCase().replace(/\s+/g, '_'),
          name: prodName,
          description: prodDesc,
          product_type: prodType
        })
      });
      if (res.ok) {
        setShowProductModal(false);
        setProdCode('');
        setProdName('');
        setProdDesc('');
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to create product');
      }
    } catch (e) {
      alert('Network error creating product');
    }
  };

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    if (!selectedProductId || !pkgCode || !pkgName) return;
    try {
      const pRes = await fetch(`${API_BASE_URL}/admin/commercial/packages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          product_id: selectedProductId,
          code: pkgCode.toUpperCase().replace(/\s+/g, '_'),
          name: pkgName,
          description: pkgDesc,
          duration_days: parseInt(pkgDays) || 7
        })
      });

      if (!pRes.ok) {
        const err = await pRes.json();
        alert(err.detail || 'Failed to create package');
        return;
      }

      const createdPkg = await pRes.json();

      // Create Version 1 Price Card (paise conversion)
      await fetch(`${API_BASE_URL}/admin/commercial/price-versions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          package_id: createdPkg.id,
          base_amount: Math.round(parseFloat(pkgPriceRupees) * 100),
          version: 1,
          currency: 'INR',
          tax_mode: 'EXCLUSIVE',
          effective_from: new Date().toISOString()
        })
      });

      setShowPkgModal(false);
      setPkgCode('');
      setPkgName('');
      setPkgDesc('');
      fetchData();
    } catch (e) {
      alert('Network error creating package');
    }
  };

  const formatRupees = (paise) => {
    if (paise === undefined || paise === null) return '₹0';
    return `₹${(paise / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return <div className="card"><p>Loading Commercial Rate Cards & Catalog...</p></div>;
  }

  return (
    <div>
      {error && (
        <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Header Actions */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3>Commercial Catalog & Rate Cards</h3>
            <p style={{ color: '#666', marginTop: '4px' }}>Define purchasable commercial products, duration packages, and versioned base rate cards.</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-outline" onClick={() => setShowProductModal(true)}>+ New Product</button>
            <button className="btn-primary" onClick={() => setShowPkgModal(true)}>+ New Package & Rate Card</button>
          </div>
        </div>
      </div>

      {/* Products & Packages Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {packages.map((pkg) => {
          const activePv = pkg.price_versions && pkg.price_versions.length > 0 ? pkg.price_versions[0] : null;
          return (
            <div key={pkg.id} className="card" style={{ borderLeft: '6px solid #FF5722' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span className="badge-info" style={{ marginBottom: '8px', display: 'inline-block' }}>{pkg.code}</span>
                  <h3 style={{ margin: '4px 0' }}>{pkg.name}</h3>
                  <p style={{ color: '#666', fontSize: '14px' }}>{pkg.description || 'No description provided.'}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2E7D32' }}>
                    {activePv ? formatRupees(activePv.base_amount) : '₹0'}
                  </div>
                  <span style={{ fontSize: '12px', color: '#777' }}>+ 18% GST (Exclusive)</span>
                  <div style={{ marginTop: '6px' }}>
                    <span className="badge-success">{pkg.duration_days} Days Duration</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '480px', background: '#fff' }}>
            <h3 style={{ marginBottom: '16px' }}>Add Commercial Product</h3>
            <form onSubmit={handleCreateProduct}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Product Code *</label>
                <input type="text" placeholder="e.g. HOME_HERO_BANNER" value={prodCode} onChange={(e) => setProdCode(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Product Name *</label>
                <input type="text" placeholder="e.g. Home Hero Banner Placement" value={prodName} onChange={(e) => setProdName(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Product Type</label>
                <select value={prodType} onChange={(e) => setProdType(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                  <option value="ADVERTISING">Advertising Banner</option>
                  <option value="SPONSORED_LISTING">Sponsored Facility Listing</option>
                  <option value="PROMOTION">Featured Facility Highlight</option>
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Description</label>
                <textarea value={prodDesc} onChange={(e) => setProdDesc(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-outline" onClick={() => setShowProductModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Product</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Package Modal */}
      {showPkgModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '480px', background: '#fff' }}>
            <h3 style={{ marginBottom: '16px' }}>Add Package & Rate Card</h3>
            <form onSubmit={handleCreatePackage}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Parent Product *</label>
                <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                  <option value="">Select Parent Product...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Package Code *</label>
                <input type="text" placeholder="e.g. HERO_BANNER_7D" value={pkgCode} onChange={(e) => setPkgCode(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Package Name *</label>
                <input type="text" placeholder="e.g. 7-Day Hero Banner Package" value={pkgName} onChange={(e) => setPkgName(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Duration (Days) *</label>
                  <input type="number" value={pkgDays} onChange={(e) => setPkgDays(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Base Price (₹ INR) *</label>
                  <input type="number" value={pkgPriceRupees} onChange={(e) => setPkgPriceRupees(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Description</label>
                <textarea value={pkgDesc} onChange={(e) => setPkgDesc(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-outline" onClick={() => setShowPkgModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Package</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CatalogManagement;
